"use client";

import { c as _c } from "react/compiler-runtime";
import type { ApiWave } from "@/generated/models/ApiWave";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { MemesSubmissionPreviewScreen } from "./preview/MemesSubmissionPreviewScreen";
import AdditionalInfoStep from "./steps/AdditionalInfoStep";
import AgreementStep from "./steps/AgreementStep";
import ArtworkStep from "./steps/ArtworkStep";
import { SubmissionStep } from "./types/Steps";
import type { SubmissionPhase } from "./ui/SubmissionProgress";
import { getResubmissionMediaTypeInfo } from "./utils/resubmissionMediaType";
import type { ArtworkSubmissionForm } from "./hooks/useArtworkSubmissionForm";
interface MemesArtSubmissionStepContentProps {
  readonly form: ArtworkSubmissionForm;
  readonly wave: ApiWave;
  readonly isPreviewMode: boolean;
  readonly previewDrop: ExtendedDrop | null;
  readonly isSubmitting: boolean;
  readonly submissionPhase: SubmissionPhase;
  readonly uploadProgress: number;
  readonly submissionError?: string | undefined;
  readonly submitLabel: string;
  readonly onClose: () => void;
  readonly onBackToEdit: () => void;
  readonly onBackFromAdditionalInfo: () => void;
  readonly onOpenPreview: () => void;
  readonly onSubmitClick: () => void;
  readonly onArtworkCommentaryMediaChange: (media: string[]) => void;
  readonly onPreviewImageChange: (url: string) => void;
  readonly onPromoVideoChange: (url: string) => void;
}
export function MemesArtSubmissionStepContent(t0) {
  const $ = _c(80);
  const {
    form,
    wave,
    isPreviewMode,
    previewDrop,
    isSubmitting,
    submissionPhase,
    uploadProgress,
    submissionError,
    submitLabel,
    onClose,
    onBackToEdit,
    onBackFromAdditionalInfo,
    onOpenPreview,
    onSubmitClick,
    onArtworkCommentaryMediaChange,
    onPreviewImageChange,
    onPromoVideoChange
  } = t0;
  let t1;
  if ($[0] !== form.selectedFile) {
    t1 = form.selectedFile ? {
      name: form.selectedFile.name,
      size: form.selectedFile.size
    } : null;
    $[0] = form.selectedFile;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const fileInfo = t1;
  let t2;
  bb0: {
    if (form.mediaSource === "upload" && form.selectedFile) {
      let t3;
      if ($[2] !== form.selectedFile.name || $[3] !== form.selectedFile.type) {
        t3 = getResubmissionMediaTypeInfo({
          mimeType: form.selectedFile.type,
          fileName: form.selectedFile.name
        });
        $[2] = form.selectedFile.name;
        $[3] = form.selectedFile.type;
        $[4] = t3;
      } else {
        t3 = $[4];
      }
      t2 = t3;
      break bb0;
    }
    if (form.mediaSource === "upload" && form.existingMedia) {
      let t3;
      if ($[5] !== form.existingMedia.mimeType || $[6] !== form.existingMedia.url) {
        t3 = getResubmissionMediaTypeInfo({
          mimeType: form.existingMedia.mimeType,
          url: form.existingMedia.url
        });
        $[5] = form.existingMedia.mimeType;
        $[6] = form.existingMedia.url;
        $[7] = t3;
      } else {
        t3 = $[7];
      }
      t2 = t3;
      break bb0;
    }
    if (form.mediaSource === "url" && form.isExternalMediaValid) {
      let t3;
      if ($[8] !== form.externalMediaMimeType || $[9] !== form.externalMediaUrl) {
        t3 = getResubmissionMediaTypeInfo({
          mimeType: form.externalMediaMimeType,
          url: form.externalMediaUrl
        });
        $[8] = form.externalMediaMimeType;
        $[9] = form.externalMediaUrl;
        $[10] = t3;
      } else {
        t3 = $[10];
      }
      t2 = t3;
      break bb0;
    }
    let t3;
    if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = {
        label: null,
        isInteractive: false
      };
      $[11] = t3;
    } else {
      t3 = $[11];
    }
    t2 = t3;
  }
  const mediaTypeInfo = t2;
  const previewRequiredMediaType = mediaTypeInfo.label;
  const requiresPreviewImage = previewRequiredMediaType !== null;
  const requiresPromoVideoOption = mediaTypeInfo.isInteractive;
  switch (form.currentStep) {
    case SubmissionStep.AGREEMENT:
      {
        let t3;
        if ($[12] !== form.agreements || $[13] !== form.handleContinueFromTerms || $[14] !== form.setAgreements || $[15] !== wave) {
          t3 = <AgreementStep wave={wave} agreements={form.agreements} setAgreements={form.setAgreements} onContinue={form.handleContinueFromTerms} />;
          $[12] = form.agreements;
          $[13] = form.handleContinueFromTerms;
          $[14] = form.setAgreements;
          $[15] = wave;
          $[16] = t3;
        } else {
          t3 = $[16];
        }
        return t3;
      }
    case SubmissionStep.ARTWORK:
      {
        const t3 = form.existingMedia?.mimeType ?? null;
        let t4;
        if ($[17] !== fileInfo || $[18] !== form.artworkUploaded || $[19] !== form.artworkUrl || $[20] !== form.clearExternalMedia || $[21] !== form.externalMediaError || $[22] !== form.externalMediaHashInput || $[23] !== form.externalMediaMimeType || $[24] !== form.externalMediaPreviewUrl || $[25] !== form.externalMediaProvider || $[26] !== form.externalMediaUrl || $[27] !== form.externalMediaValidationStatus || $[28] !== form.handleContinueFromArtwork || $[29] !== form.handleFileSelect || $[30] !== form.isExternalMediaValid || $[31] !== form.mediaSource || $[32] !== form.setArtworkUploaded || $[33] !== form.setExternalMediaHash || $[34] !== form.setExternalMediaMimeType || $[35] !== form.setExternalMediaProvider || $[36] !== form.setMediaSource || $[37] !== form.setTraits || $[38] !== form.traits || $[39] !== form.updateTraitField || $[40] !== form.uploadError || $[41] !== isSubmitting || $[42] !== onClose || $[43] !== submissionError || $[44] !== submissionPhase || $[45] !== t3 || $[46] !== uploadProgress) {
          t4 = <ArtworkStep traits={form.traits} artworkUploaded={form.artworkUploaded} artworkUrl={form.artworkUrl} uploadError={form.uploadError} artworkMimeType={t3} setArtworkUploaded={form.setArtworkUploaded} handleFileSelect={form.handleFileSelect} mediaSource={form.mediaSource} setMediaSource={form.setMediaSource} externalHash={form.externalMediaHashInput} externalProvider={form.externalMediaProvider} externalConstructedUrl={form.externalMediaUrl} externalPreviewUrl={form.externalMediaPreviewUrl} externalMimeType={form.externalMediaMimeType} externalError={form.externalMediaError} externalValidationStatus={form.externalMediaValidationStatus} isExternalMediaValid={form.isExternalMediaValid} onExternalHashChange={form.setExternalMediaHash} onExternalProviderChange={form.setExternalMediaProvider} onExternalMimeTypeChange={form.setExternalMediaMimeType} onClearExternalMedia={form.clearExternalMedia} onSubmit={form.handleContinueFromArtwork} onCancel={onClose} updateTraitField={form.updateTraitField} setTraits={form.setTraits} isSubmitting={isSubmitting} submissionPhase={submissionPhase} uploadProgress={uploadProgress} fileInfo={fileInfo} submissionError={submissionError} />;
          $[17] = fileInfo;
          $[18] = form.artworkUploaded;
          $[19] = form.artworkUrl;
          $[20] = form.clearExternalMedia;
          $[21] = form.externalMediaError;
          $[22] = form.externalMediaHashInput;
          $[23] = form.externalMediaMimeType;
          $[24] = form.externalMediaPreviewUrl;
          $[25] = form.externalMediaProvider;
          $[26] = form.externalMediaUrl;
          $[27] = form.externalMediaValidationStatus;
          $[28] = form.handleContinueFromArtwork;
          $[29] = form.handleFileSelect;
          $[30] = form.isExternalMediaValid;
          $[31] = form.mediaSource;
          $[32] = form.setArtworkUploaded;
          $[33] = form.setExternalMediaHash;
          $[34] = form.setExternalMediaMimeType;
          $[35] = form.setExternalMediaProvider;
          $[36] = form.setMediaSource;
          $[37] = form.setTraits;
          $[38] = form.traits;
          $[39] = form.updateTraitField;
          $[40] = form.uploadError;
          $[41] = isSubmitting;
          $[42] = onClose;
          $[43] = submissionError;
          $[44] = submissionPhase;
          $[45] = t3;
          $[46] = uploadProgress;
          $[47] = t4;
        } else {
          t4 = $[47];
        }
        return t4;
      }
    case SubmissionStep.ADDITIONAL_INFO:
      {
        if (isPreviewMode && previewDrop) {
          let t3;
          if ($[48] !== isSubmitting || $[49] !== onBackToEdit || $[50] !== onSubmitClick || $[51] !== previewDrop || $[52] !== submitLabel) {
            t3 = <MemesSubmissionPreviewScreen previewDrop={previewDrop} onBackToEdit={onBackToEdit} onSubmit={onSubmitClick} isSubmitting={isSubmitting} submitLabel={submitLabel} />;
            $[48] = isSubmitting;
            $[49] = onBackToEdit;
            $[50] = onSubmitClick;
            $[51] = previewDrop;
            $[52] = submitLabel;
            $[53] = t3;
          } else {
            t3 = $[53];
          }
          return t3;
        }
        let t3;
        if ($[54] !== form.operationalData.about_artist || $[55] !== form.operationalData.additional_media.artwork_commentary_media || $[56] !== form.operationalData.additional_media.preview_image || $[57] !== form.operationalData.additional_media.promo_video || $[58] !== form.operationalData.airdrop_config || $[59] !== form.operationalData.allowlist_batches || $[60] !== form.operationalData.commentary || $[61] !== form.operationalData.payment_info || $[62] !== form.setAboutArtist || $[63] !== form.setAirdropConfig || $[64] !== form.setAllowlistBatches || $[65] !== form.setCommentary || $[66] !== form.setPaymentInfo || $[67] !== form.traits || $[68] !== isSubmitting || $[69] !== onArtworkCommentaryMediaChange || $[70] !== onBackFromAdditionalInfo || $[71] !== onOpenPreview || $[72] !== onPreviewImageChange || $[73] !== onPromoVideoChange || $[74] !== onSubmitClick || $[75] !== previewRequiredMediaType || $[76] !== requiresPreviewImage || $[77] !== requiresPromoVideoOption || $[78] !== submitLabel) {
          t3 = <AdditionalInfoStep traits={form.traits} airdropEntries={form.operationalData.airdrop_config} onAirdropEntriesChange={form.setAirdropConfig} paymentInfo={form.operationalData.payment_info} onPaymentInfoChange={form.setPaymentInfo} allowlistBatches={form.operationalData.allowlist_batches} supportingMedia={form.operationalData.additional_media.artwork_commentary_media} artworkCommentary={form.operationalData.commentary} aboutArtist={form.operationalData.about_artist} previewImage={form.operationalData.additional_media.preview_image} promoVideo={form.operationalData.additional_media.promo_video} requiresPreviewImage={requiresPreviewImage} requiresPromoVideoOption={requiresPromoVideoOption} previewRequiredMediaType={previewRequiredMediaType} onBatchesChange={form.setAllowlistBatches} onSupportingMediaChange={onArtworkCommentaryMediaChange} onPreviewImageChange={onPreviewImageChange} onPromoVideoChange={onPromoVideoChange} onArtworkCommentaryChange={form.setCommentary} onAboutArtistChange={form.setAboutArtist} onBack={onBackFromAdditionalInfo} onPreview={onOpenPreview} onSubmit={onSubmitClick} isSubmitting={isSubmitting} submitLabel={submitLabel} />;
          $[54] = form.operationalData.about_artist;
          $[55] = form.operationalData.additional_media.artwork_commentary_media;
          $[56] = form.operationalData.additional_media.preview_image;
          $[57] = form.operationalData.additional_media.promo_video;
          $[58] = form.operationalData.airdrop_config;
          $[59] = form.operationalData.allowlist_batches;
          $[60] = form.operationalData.commentary;
          $[61] = form.operationalData.payment_info;
          $[62] = form.setAboutArtist;
          $[63] = form.setAirdropConfig;
          $[64] = form.setAllowlistBatches;
          $[65] = form.setCommentary;
          $[66] = form.setPaymentInfo;
          $[67] = form.traits;
          $[68] = isSubmitting;
          $[69] = onArtworkCommentaryMediaChange;
          $[70] = onBackFromAdditionalInfo;
          $[71] = onOpenPreview;
          $[72] = onPreviewImageChange;
          $[73] = onPromoVideoChange;
          $[74] = onSubmitClick;
          $[75] = previewRequiredMediaType;
          $[76] = requiresPreviewImage;
          $[77] = requiresPromoVideoOption;
          $[78] = submitLabel;
          $[79] = t3;
        } else {
          t3 = $[79];
        }
        return t3;
      }
  }
  return null;
}